import React, { useCallback, useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const SWIPE_THRESHOLD_PX = 50;

type TabletColumnSwipeProps = {
  groupIndex: number;
  groupCount: number;
  onGroupChange: (index: number) => void;
  children: React.ReactNode;
};

const TabletColumnSwipe: React.FC<TabletColumnSwipeProps> = ({
  groupIndex,
  groupCount,
  onGroupChange,
  children,
}) => {
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= groupCount) return;
      onGroupChange(index);
    },
    [groupCount, onGroupChange],
  );

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  }, []);

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      if (touchStartX.current == null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
      const delta = endX - touchStartX.current;
      touchStartX.current = null;

      if (delta < -SWIPE_THRESHOLD_PX) {
        goTo(groupIndex + 1);
      } else if (delta > SWIPE_THRESHOLD_PX) {
        goTo(groupIndex - 1);
      }
    },
    [goTo, groupIndex],
  );

  if (groupCount <= 1) {
    return <>{children}</>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0.5,
          py: 0.25,
          minHeight: 28,
        }}
      >
        <IconButton
          size="small"
          aria-label="Previous columns"
          disabled={groupIndex === 0}
          onClick={() => goTo(groupIndex - 1)}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {Array.from({ length: groupCount }).map((_, index) => (
            <Box
              key={index}
              role="button"
              tabIndex={0}
              aria-label={`Show column group ${index + 1} of ${groupCount}`}
              aria-current={index === groupIndex ? "true" : undefined}
              onClick={() => goTo(index)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  goTo(index);
                }
              }}
              sx={{
                width: index === groupIndex ? 16 : 6,
                height: 6,
                borderRadius: 3,
                bgcolor:
                  index === groupIndex
                    ? "primary.main"
                    : "action.disabledBackground",
                cursor: "pointer",
                transition: "width 0.2s ease",
              }}
            />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {groupIndex + 1}/{groupCount}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Next columns"
          disabled={groupIndex >= groupCount - 1}
          onClick={() => goTo(groupIndex + 1)}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{ touchAction: "pan-y" }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default TabletColumnSwipe;
